import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Image as ImageIcon, Loader } from 'lucide-react';
import { fetchCategories } from '../redux/CategorySlice';
import { addProduct, editProduct } from '../redux/ProductSlice';


const AddProductForm = ({ setShowModal, editingProduct, setEditingProduct, setFormData, formData }) => {
    const dispatch = useDispatch();

    // Select categories from Redux store
    const categories = useSelector((state) => state.categories.categories);
    const categoriesLoading = useSelector((state) => state.categories.loading);

    const [mainImagePreview, setMainImagePreview] = useState('');
    const [backupImagePreview, setBackupImagePreview] = useState('');
    const [otherImagesPreview, setOtherImagesPreview] = useState([]);
    const [mainDragOver, setMainDragOver] = useState(false);
    const [backupDragOver, setBackupDragOver] = useState(false);
    const [otherDragOver, setOtherDragOver] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [convertingImages, setConvertingImages] = useState(false);

    const MAX_OTHER_IMAGES = 5;

    // Fetch categories on component mount
    useEffect(() => {
        dispatch(fetchCategories());
    }, [dispatch]);

    // Initialize image previews when editing
    useEffect(() => {
        if (editingProduct) {
            setMainImagePreview(formData.image || '');
            setBackupImagePreview(formData.backupImage || '');
            setOtherImagesPreview(formData.otherImages || []);
        }
    }, [editingProduct, formData.image, formData.backupImage, formData.otherImages]);

    const statuses = ['Active', 'Inactive', 'Out of Stock'];   
    
    

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'category') {
            setFormData(prev => ({
                ...prev,
                [name]: value ? Number(value) : ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Handle main image upload
    const handleMainImageUpload = (file) => {
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const imageUrl = e.target.result;
                setMainImagePreview(imageUrl);
                setFormData(prev => ({
                    ...prev,
                    image: imageUrl
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle backup image upload
    const handleBackupImageUpload = (file) => {
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const imageUrl = e.target.result;
                setBackupImagePreview(imageUrl);
                setFormData(prev => ({
                    ...prev,
                    backupImage: imageUrl
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle other images upload (multiple)
    const handleOtherImagesUpload = async (files) => {
        const fileArray = Array.from(files);
        const remainingSlots = MAX_OTHER_IMAGES - otherImagesPreview.length;
        
        if (fileArray.length > remainingSlots) {
            alert(`You can only add ${remainingSlots} more image(s). Maximum is ${MAX_OTHER_IMAGES} images.`);
            return;
        }

        setConvertingImages(true);

        const newImages = [];
        for (const file of fileArray) {
            if (file.type.startsWith('image/')) {
                const imageUrl = await new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target.result);
                    reader.readAsDataURL(file);
                });
                newImages.push(imageUrl);
            }
        }

        setConvertingImages(false);

        const updatedImages = [...otherImagesPreview, ...newImages];
        setOtherImagesPreview(updatedImages);
        setFormData(prev => ({
            ...prev,
            otherImages: updatedImages
        }));
    };

    // Handle main image URL input
    const handleMainImageUrl = (e) => {
        const url = e.target.value;
        setMainImagePreview(url);
        setFormData(prev => ({
            ...prev,
            image: url
        }));
    };

    // Handle backup image URL input
    const handleBackupImageUrl = (e) => {
        const url = e.target.value;
        setBackupImagePreview(url);
        setFormData(prev => ({
            ...prev,
            backupImage: url
        }));
    };

    // Handle drag and drop for main image
    const handleMainDrop = (e) => {
        e.preventDefault();
        setMainDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleMainImageUpload(files[0]);
        }
    };

    const handleMainDragOver = (e) => {
        e.preventDefault();
        setMainDragOver(true);
    };

    const handleMainDragLeave = (e) => {
        e.preventDefault();
        setMainDragOver(false);
    };

    // Handle drag and drop for backup image
    const handleBackupDrop = (e) => {
        e.preventDefault();
        setBackupDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleBackupImageUpload(files[0]);
        }
    };

    const handleBackupDragOver = (e) => {
        e.preventDefault();
        setBackupDragOver(true);
    };

    const handleBackupDragLeave = (e) => {
        e.preventDefault();
        setBackupDragOver(false);
    };

    // Handle drag and drop for other images
    const handleOtherDrop = (e) => {
        e.preventDefault();
        setOtherDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            handleOtherImagesUpload(files);
        }
    };

    const handleOtherDragOver = (e) => {
        e.preventDefault();
        setOtherDragOver(true);
    };

    const handleOtherDragLeave = (e) => {
        e.preventDefault();
        setOtherDragOver(false);
    };

    // Handle file input change for main image
    const handleMainFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleMainImageUpload(file);
        }
    };

    // Handle file input change for backup image
    const handleBackupFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleBackupImageUpload(file);
        }
    };

    // Handle file input change for other images
    const handleOtherFileChange = (e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleOtherImagesUpload(files);
        }
    };

    // Remove main image
    const removeMainImage = () => {
        setMainImagePreview('');
        setFormData(prev => ({
            ...prev,
            image: ''
        }));
    };

    // Remove backup image
    const removeBackupImage = () => {
        setBackupImagePreview('');
        setFormData(prev => ({
            ...prev,
            backupImage: ''
        }));
    };

    // Remove specific other image
    const removeOtherImage = (index) => {
        const updatedImages = otherImagesPreview.filter((_, i) => i !== index);
        setOtherImagesPreview(updatedImages);
        setFormData(prev => ({
            ...prev,
            otherImages: updatedImages
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const featuresArray = typeof formData.features === 'string'
                ? formData.features.split(',').map(f => f.trim()).filter(f => f)
                : formData.features;
            // Prepare product data for API
            const productData = {
                productName: formData.name,
                categoryid: formData.category,
                price: Number(formData.price),
                stock: Number(formData.stock),
                status: formData.status,
                description: formData.description,
                story: formData.story,
                primary_feature: formData.topNotes.split(',').map(f => f.trim()).filter(f => f),
                secondary_feature: formData.middleNotes.split(',').map(f => f.trim()).filter(f => f),
                core_feature: formData.baseNotes.split(',').map(f => f.trim()).filter(f => f),
                width: formData.diameter,
                height: formData.height,
                weight: formData.netWeight,
                burn_duration: formData.burnTime,
                key_feature: featuresArray,
                main_image: formData.image,
                backup_image: formData.backupImage,
                otherImages: formData.otherImages || [],
                productToken: formData.productToken
            };

            if (editingProduct) {
                // Update existing product
                await dispatch(editProduct({
                    productToken: editingProduct.productToken,
                    productData
                })).unwrap();
            } else {
                // Add new product
                await dispatch(addProduct(productData)).unwrap();
            }

            resetForm();
        } catch (error) {
            console.error('Error submitting product:', error);
            alert(error || 'Failed to submit product');
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            category: '',
            price: '',
            stock: '',
            status: 'Active',
            description: '',
            story: '',
            topNotes: '',
            middleNotes: '',
            baseNotes: '',
            diameter: '',
            height: '',
            netWeight: '',
            burnTime: '',
            features: [],
            image: '',
            backupImage: '',
            otherImages: []
        });
        setMainImagePreview('');
        setBackupImagePreview('');
        setOtherImagesPreview([]);
        setEditingProduct(null);
        setShowModal(false);
    };

    // Image upload component
    const ImageUploadSection = ({
        title,
        preview,
        onDrop,
        onDragOver,
        onDragLeave,
        onFileChange,
        onUrlChange,
        onRemove,
        dragOver,
        inputId,
        isRequired = false
    }) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
                {title} {isRequired && '*'}
            </label>

            {preview ? (
                <div className="relative inline-block mb-4">
                    <img
                        src={preview}
                        alt="Product preview"
                        className="w-full max-w-sm h-48 object-cover rounded-lg border border-gray-300"
                    />
                    <button
                        type="button"
                        onClick={onRemove}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                        <X size={14} />
                    </button>
                </div>
            ) : (
                <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors mb-4 ${dragOver
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-300 hover:border-gray-400'
                        }`}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                >
                    <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
                    <div className="space-y-2">
                        <p className="text-gray-600">
                            <label htmlFor={inputId} className="cursor-pointer text-amber-600 hover:text-amber-700">
                                Click to upload
                            </label> or drag and drop
                        </p>
                        <p className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                    <input
                        id={inputId}
                        type="file"
                        accept="image/*"
                        onChange={onFileChange}
                        className="hidden"
                    />
                </div>
            )}

            {/* OR separator */}
            <div className="flex items-center my-4">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-3 text-sm text-gray-500">OR</span>
                <div className="flex-1 border-t border-gray-200"></div>
            </div>

            {/* Image URL Input */}
            <div className="mb-6">
                <input
                    type="url"
                    placeholder="Enter image URL"
                    onChange={onUrlChange}
                    value={preview}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                />
            </div>
        </div>
    );

    return (
        <div className="bg-white rounded-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Product Images Upload */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <ImageUploadSection
                            title="Main Product Image"
                            preview={mainImagePreview}
                            onDrop={handleMainDrop}
                            onDragOver={handleMainDragOver}
                            onDragLeave={handleMainDragLeave}
                            onFileChange={handleMainFileChange}
                            onUrlChange={handleMainImageUrl}
                            onRemove={removeMainImage}
                            dragOver={mainDragOver}
                            inputId="main-image-upload"
                            isRequired={true}
                        />

                        <ImageUploadSection
                            title="Backup Product Image"
                            preview={backupImagePreview}
                            onDrop={handleBackupDrop}
                            onDragOver={handleBackupDragOver}
                            onDragLeave={handleBackupDragLeave}
                            onFileChange={handleBackupFileChange}
                            onUrlChange={handleBackupImageUrl}
                            onRemove={removeBackupImage}
                            dragOver={backupDragOver}
                            inputId="backup-image-upload"
                        />
                    </div>

                    {/* Other Images Section (Multiple) */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Additional Product Images (Max {MAX_OTHER_IMAGES})
                        </label>
                        <p className="text-xs text-gray-500 mb-4">
                            {otherImagesPreview.length}/{MAX_OTHER_IMAGES} images uploaded
                        </p>

                        {/* Converting Loader */}
                        {convertingImages && (
                            <div className="flex items-center justify-center gap-2 mb-4 p-4 bg-amber-50 rounded-lg">
                                <Loader className="animate-spin text-amber-600" size={20} />
                                <span className="text-sm text-amber-700">Converting images...</span>
                            </div>
                        )}

                        {/* Image Grid */}
                        {otherImagesPreview.length > 0 && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-4">
                                {otherImagesPreview.map((img, index) => (
                                    <div key={index} className="relative group">
                                        <img
                                            src={img}
                                            alt={`Product ${index + 1}`}
                                            className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeOtherImage(index)}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload Area (only show if not at max) */}
                        {otherImagesPreview.length < MAX_OTHER_IMAGES && (
                            <div
                                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                                    otherDragOver
                                        ? 'border-amber-500 bg-amber-50'
                                        : 'border-gray-300 hover:border-gray-400'
                                }`}
                                onDrop={handleOtherDrop}
                                onDragOver={handleOtherDragOver}
                                onDragLeave={handleOtherDragLeave}
                            >
                                <ImageIcon size={48} className="mx-auto text-gray-400 mb-4" />
                                <div className="space-y-2">
                                    <p className="text-gray-600">
                                        <label
                                            htmlFor="other-images-upload"
                                            className="cursor-pointer text-amber-600 hover:text-amber-700"
                                        >
                                            Click to upload
                                        </label>{' '}
                                        or drag and drop
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        PNG, JPG, GIF up to 10MB (Max {MAX_OTHER_IMAGES - otherImagesPreview.length} more)
                                    </p>
                                </div>
                                <input
                                    id="other-images-upload"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleOtherFileChange}
                                    className="hidden"
                                    disabled={convertingImages}
                                />
                            </div>
                        )}
                    </div>

                    {/* Basic Information */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Product Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category *
                            </label>
                            <select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                                disabled={categoriesLoading}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                            >
                                <option value="">
                                    {categoriesLoading ? 'Loading categories...' : 'Select Category'}
                                </option>
                                {categories.map((cat) => (
                                    <option key={cat.categoryid || cat.id} value={cat.categoryid}>
                                        {cat.category || cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Product Description
                        </label>
                        <textarea
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            rows="3"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Brief description of the product..."
                        />
                    </div>

                    {/* Story */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Product Story
                        </label>
                        <textarea
                            name="story"
                            value={formData.story}
                            onChange={handleInputChange}
                            rows="4"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="Tell the story behind this product..."
                        />
                    </div>

                    {/* Olfactive Notes / Features */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Top Notes / Primary Features
                            </label>
                            <input
                                type="text"
                                name="topNotes"
                                value={formData.topNotes}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., Shea butter, Honey"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Middle Notes / Secondary Features
                            </label>
                            <input
                                type="text"
                                name="middleNotes"
                                value={formData.middleNotes}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., Floral, Nut, Spice"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Base Notes / Core Features
                            </label>
                            <input
                                type="text"
                                name="baseNotes"
                                value={formData.baseNotes}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., Cedar, Musk, Vanilla"
                            />
                        </div>
                    </div>

                    {/* Product Details */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Price (£) *
                            </label>
                            <input
                                type="number"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                required
                                min="0"
                                step="0.01"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Stock *
                            </label>
                            <input
                                type="number"
                                name="stock"
                                value={formData.stock}
                                onChange={handleInputChange}
                                required
                                min="0"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Diameter/Width
                            </label>
                            <input
                                type="text"
                                name="diameter"
                                value={formData.diameter}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., 3.11 inches"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Height
                            </label>
                            <input
                                type="text"
                                name="height"
                                value={formData.height}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., 3.66 inches"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Net Weight
                            </label>
                            <input
                                type="text"
                                name="netWeight"
                                value={formData.netWeight}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., 250g"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Burn Time / Duration
                            </label>
                            <input
                                type="text"
                                name="burnTime"
                                value={formData.burnTime}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                placeholder="e.g., 80 hours"
                            />
                        </div>

                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status *
                            </label>
                            <select
                                name="status"
                                value={formData.status}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            >
                                {statuses.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Features */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Key Features
                        </label>
                        <input
                            type="text"
                            name="features"
                            value={typeof formData.features === 'string'
                                ? formData.features
                                : (Array.isArray(formData.features) ? formData.features.join(', ') : '')
                            }
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                            placeholder="e.g., Cruelty-Free & Vegan, Clean Burn Cotton Wicks, Made in USA (separate with commas)"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separate multiple features with commas</p>
                    </div>

                    <div className="flex space-x-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={resetForm}
                            disabled={isSubmitting}
                            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || convertingImages}
                            className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Submitting...' : (editingProduct ? 'Update Product' : 'Add Product')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddProductForm;